import type { IconName } from '@hotjar/hotsauce';
import { Badge, tokens, Icon, Tooltip, Text, CloseAction } from '@hotjar/hotsauce';
import { forwardRef, useRef, useState, useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import styled from '@emotion/styled';
import type { AllBadgeStatus } from '@hotjar/hotsauce/build/components/Badge/BaseBadge';

const CloseContainer = styled.div<{ isFunnelStep: boolean }>`
  position: absolute;
  right: 1px;
  top: 1px;
  border-radius: ${tokens.radius.m};
  padding: ${tokens.spacer.x0};
  background: linear-gradient(
    to right,
    transparent 0%,
    ${({ isFunnelStep }) => (isFunnelStep ? 'white' : tokens.color.bg_active)} 18%
  );
  ${tokens.transition()};
  opacity: 0;
`;

const Wrapper = styled.div<{ isFunnelStep: boolean }>`
  position: relative;
  ${({ isFunnelStep }) => (isFunnelStep ? 'min-width: 0;' : '')};

  &:hover,
  &:focus-within {
    ${CloseContainer} {
      opacity: 1;
    }
  }
`;

const TagIcon = styled(Icon)`
  color: ${tokens.color.text_default};
  margin-right: ${tokens.spacer.x1};
  filter: grayscale(100%);
`;

const DropdownIcon = styled(Icon)`
  color: ${tokens.color.text_default};
  margin-left: 11px;
  position: relative;
  top: -1px;
`;

const Button = styled.button<{
  isDropdownVisible: boolean;
  isInactive: boolean;
  isAutoSelected: boolean;
  isFunnelStep: boolean;
  disabled: boolean;
}>`
  display: inline-flex;
  align-items: center;
  border: 0;
  outline: none;
  box-shadow: none;
  ${tokens.transition()};
  cursor: pointer;
  white-space: nowrap;
  border-radius: ${tokens.radius.m};
  border: 1px solid transparent;
  padding: ${tokens.spacer.x1} ${tokens.spacer.x2};
  background: ${({ isInactive, isAutoSelected, isFunnelStep }) => {
    if (isInactive) return 'transparent';
    if (isAutoSelected) return tokens.color.bg_warning;
    if (isFunnelStep) return tokens.color.white;
    return tokens.color.bg_active;
  }};
  border-color: ${({ isDropdownVisible, isInactive, isFunnelStep }) => {
    if (isDropdownVisible) return tokens.color.ui_border_active;
    if (isInactive || isFunnelStep) return tokens.color.ui_border;
    return 'transparent';
  }};
  ${({ isFunnelStep }) => (isFunnelStep ? 'max-width: 100%;' : '')};

  &:hover {
    border-color: ${({ disabled }) => {
      if (disabled) return tokens.color.ui_disabled;
      return tokens.color.ui_border_active;
    }};
  }

  &:focus {
    ${tokens.focus()};
  }

  &:active {
    ${tokens.elevation(-1)};
  }

  &:disabled {
    background: ${tokens.color.ui_disabled};
    border-color: ${tokens.color.ui_disabled};
    color: ${tokens.color.text_disabled};

    ${Text} {
      color: ${tokens.color.text_disabled};
    }

    ${TagIcon} {
      fill: ${tokens.color.text_disabled};
    }

    ${DropdownIcon} {
      fill: ${tokens.color.text_disabled};
    }
  }
`;

const StyledBadge = styled(Badge)`
  margin-left: ${tokens.spacer.x1};
`;

const StyledLabel2 = styled(Text)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

interface FilterTagProps {
  filterName: string;
  label1?: string;
  label2?: string;
  autoFocus?: boolean;
  iconName?: IconName;
  showDropdownCaret?: boolean;
  onRemove?: () => void;
  focusDropdownContent?: () => void;
  isDropdownVisible?: boolean;
  isInactive?: boolean;
  maxLabelLength?: number;
  isAutoSelected?: boolean;
  isDisabled?: boolean;
  disabledReason?: string;
  badge?: AllBadgeStatus;
  isFunnelStep?: boolean;
}

const MAX_LABEL_LENGTH = 30;

export const FilterTag = forwardRef<HTMLButtonElement, FilterTagProps>(
  (
    {
      filterName,
      label1,
      label2,
      autoFocus,
      iconName,
      showDropdownCaret,
      focusDropdownContent,
      onRemove,
      isDropdownVisible,
      isInactive,
      maxLabelLength = MAX_LABEL_LENGTH,
      isAutoSelected,
      isDisabled = false,
      disabledReason,
      badge,
      isFunnelStep = false,
    }: FilterTagProps,
    ref,
  ): JSX.Element => {
    const label2Ref = useRef<HTMLParagraphElement>(null);
    const [isFunnelStepTruncated, setIsFunnelStepTruncated] = useState(false);

    useEffect(() => {
      const shouldBeTruncated =
        label2Ref?.current !== null &&
        label2Ref.current.offsetWidth < label2Ref.current.scrollWidth;
      setIsFunnelStepTruncated(shouldBeTruncated);
      // `label2Ref?.current` is added to dependency list to check if truncation was applied during the first render
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [label2, label2Ref?.current]);

    const handleRemove = () => {
      onRemove?.();
    };

    const type = isFunnelStep ? 'step' : 'filter';
    const clearLabel = `Clear ${type}`;
    const isFullWidthFunnelStep = isFunnelStep && label2Ref?.current !== null;
    const showTooltip = isFullWidthFunnelStep
      ? isFunnelStepTruncated
      : label2 != null && label2.length > maxLabelLength;

    const truncatedLabel2 =
      showTooltip && !isFullWidthFunnelStep ? `${label2?.slice(0, maxLabelLength - 3)}...` : label2;

    const onKeyUp = (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        handleRemove();
      }

      if (event.key === 'ArrowDown' && isDropdownVisible === true) {
        focusDropdownContent?.();
      }
    };

    return (
      <Tooltip
        text={disabledReason ?? ''}
        isDisabled={isDisabled !== true || disabledReason === undefined}
      >
        <Wrapper isFunnelStep={isFunnelStep}>
          <Button
            autoFocus={autoFocus}
            ref={ref}
            role="button"
            data-testid="filter-dropdown-toggle"
            aria-label={filterName}
            isDropdownVisible={isDropdownVisible === true}
            isInactive={isInactive === true}
            isAutoSelected={isAutoSelected === true}
            isFunnelStep={isFunnelStep}
            disabled={isDisabled === true}
            onKeyUp={onKeyUp}
          >
            {iconName && <TagIcon icon={iconName} />}
            {label1 == null && label2 == null && (
              <Text textType="subtitle3">{filterName}&nbsp;</Text>
            )}
            {label1 != null && <Text textType="subtitle3">{label1}&nbsp;</Text>}
            {label2 != null && (
              <Tooltip text={label2 || ''} isDisabled={!showTooltip}>
                <StyledLabel2 textType="caption" ref={label2Ref}>
                  {truncatedLabel2}
                </StyledLabel2>
              </Tooltip>
            )}
            {badge !== undefined && <StyledBadge kind={badge} size="s" />}
            {showDropdownCaret === true && <DropdownIcon icon="caret_down_alt" />}
          </Button>

          {onRemove && (
            <Tooltip text={clearLabel}>
              <CloseContainer isFunnelStep={isFunnelStep}>
                <CloseAction
                  description={clearLabel}
                  aria-label={clearLabel}
                  data-testid={`clear-${type}-button`}
                  onClick={handleRemove}
                />
              </CloseContainer>
            </Tooltip>
          )}
        </Wrapper>
      </Tooltip>
    );
  },
);

FilterTag.displayName = 'FilterTag';
